import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useStaffGate } from '../../lib/staffContext';
import { StaffShell } from '../../ui/StaffShell';
import { ContextError } from '../../ui/ContextError';
import { Pill } from '../../ui/DataBits';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { roleLabel } from '../../ui/roles';
import { errorText, rpc, type AppError } from '../admin/api';

type Req = { id: string; class_label: string; grade: string | null; topics: string[]; status: 'open' | 'acknowledged' | 'done'; created_at: string; requested_by: string | null };
// 0026, spec §14.10: an anonymous Safety Valve signal — tied to a class/
// session, never a student. No topics, no requested_by (there is no
// requester). Deliberately bypasses the usual >=5 suppression floor, so it
// can appear even when `requests` above would be empty.
type Sig = { id: string; class_label: string; grade: string | null; status: 'open' | 'acknowledged' | 'done'; created_at: string; updated_at: string };

// I7: the counsellor's queue of class-level visit requests. Topics are the
// ones the teacher chose among those shown for that check-in; no results,
// no names of students.
export function CounsellorHome() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const announce = useAnnounce();
  const { ctx, error } = useStaffGate(['counsellor']);
  const [data, setData] = useState<{ scope_grades: string[] | null; requests: Req[]; safety_signals: Sig[] } | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const load = useCallback(async () => {
    const r = await rpc<NonNullable<typeof data>>('counsellor_home');
    if (r.error) setErr(r.error);
    else setData(r.data);
  }, []);
  useEffect(() => {
    if (ctx) load();
  }, [ctx, load]);
  async function update(id: string, status: 'acknowledged' | 'done') {
    const r = await rpc('counsellor_update_request', { p_request_id: id, p_status: status });
    if (r.error) setErr(r.error);
    else {
      announce(t[`co.${status}Done` as 'co.doneDone']);
      load();
    }
  }
  async function updateSignal(id: string, status: 'acknowledged' | 'done') {
    const r = await rpc('counsellor_update_safety_signal', { p_signal_id: id, p_status: status });
    if (r.error) setErr(r.error);
    else {
      announce(t[`co.${status}Done` as 'co.doneDone']);
      load();
    }
  }
  if (error) return <ContextError message={error} />;
  if (!ctx || !ctx.active_role) return <main className="page-loading" role="status">{t.loading}</main>;
  const fmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });
  return (
    <StaffShell
      role={roleLabel(t, ctx.active_role)}
      nav={[{ icon: '🤝', label: t['co.nav'], href: '/counsellor', active: true }, { icon: '🫶', label: t['r3.me.nav'], href: '/me/checkin' }]}
      title={t['co.title']}
      who={ctx.display_name}
      ctx={ctx}
      onSignOut={async () => {
        await supabase.auth.signOut();
        navigate('/teacher/login');
      }}
    >
      <p className="notice notice-block">{t['co.intro']}{data?.scope_grades ? ` ${t['h2.scopeGrades'](data.scope_grades.join(', '))}` : ''}</p>
      {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
      {!data && !err && <p role="status">{t.loading}</p>}
      {data && data.requests.length === 0 && data.safety_signals.length === 0 && <div className="panel empty">{t['co.none']}</div>}
      {data && data.safety_signals.length > 0 && (
        <section aria-labelledby="co-sv-title">
          <h2 className="panel-title" id="co-sv-title">{t['r6.cs.title']}</h2>
          <p className="form-hint">{t['r6.cs.hint']}</p>
          <ul className="approval-list">
            {data.safety_signals.map((s) => (
              <li key={s.id} className="approval">
                <div className="panel-head">
                  <h3 className="panel-title">{s.class_label}{s.grade ? ` · ${t['h2.grade'](s.grade)}` : ''}</h3>
                  <Pill kind={s.status === 'open' ? 'warn' : s.status === 'done' ? 'good' : 'neutral'}>{t[`co.st.${s.status}` as 'co.st.open']}</Pill>
                </div>
                <p className="text-muted">{t['r6.cs.flagged'](fmt.format(new Date(s.created_at)))}</p>
                {s.status !== 'done' && (
                  <div className="form-actions">
                    {s.status === 'open' && <button type="button" className="btn btn-ghost" onClick={() => updateSignal(s.id, 'acknowledged')}>{t['co.ack']}<span className="visually-hidden"> – {s.class_label}</span></button>}
                    <button type="button" className="btn btn-primary" onClick={() => updateSignal(s.id, 'done')}>{t['co.done']}<span className="visually-hidden"> – {s.class_label}</span></button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {data && data.requests.length > 0 && (
        <ul className="approval-list">
          {data.requests.map((r) => (
            <li key={r.id} className="approval">
              <div className="panel-head">
                <h2 className="panel-title">{t['co.visit'](r.class_label)}</h2>
                <Pill kind={r.status === 'open' ? 'warn' : r.status === 'done' ? 'good' : 'neutral'}>{t[`co.st.${r.status}` as 'co.st.open']}</Pill>
              </div>
              <p>{r.topics.length ? t['co.topics'](r.topics.map((x) => t[`topic.${x}` as 'topic.exams']).join(', ')) : t['co.noTopics']}</p>
              <p className="text-muted">{t['co.asked'](r.requested_by ?? '–', fmt.format(new Date(r.created_at)))}</p>
              {r.status !== 'done' && (
                <div className="form-actions">
                  {r.status === 'open' && <button type="button" className="btn btn-ghost" onClick={() => update(r.id, 'acknowledged')}>{t['co.ack']}<span className="visually-hidden"> – {r.class_label}</span></button>}
                  <button type="button" className="btn btn-primary" onClick={() => update(r.id, 'done')}>{t['co.done']}<span className="visually-hidden"> – {r.class_label}</span></button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </StaffShell>
  );
}
